import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { Show, useUser, useClerk } from "@clerk/react";
import { basePath } from "@/lib/clerk";

const NAV_LINKS = [
  { href: "/pulse",             label: "Pulse",       match: (l: string) => l === "/pulse" },
  { href: "/quiz/personality", label: "Personality", match: (l: string) => l === "/quiz/personality" },
  { href: "/quiz/decision",    label: "Decision",    match: (l: string) => l === "/quiz/decision" },
  { href: "/learn",            label: "Learn",       match: (l: string) => l === "/learn" || l.startsWith("/learn/") },
  { href: "/insights",         label: "Insights",    match: (l: string) => l.startsWith("/insights") },
  { href: "/scan",             label: "Scan",        match: (l: string) => l === "/scan" },
  { href: "/aura-scanner",     label: "Aura",        match: (l: string) => l === "/aura-scanner" },
  { href: "/background-maker", label: "Backgrounds", match: (l: string) => l === "/background-maker" },
  { href: "/color-grabber",    label: "Color Grabber", match: (l: string) => l === "/color-grabber" },
  { href: "/color-sandbox",    label: "Sandbox",       match: (l: string) => l === "/color-sandbox" },
  { href: "/paint-visualizer", label: "Paint",         match: (l: string) => l === "/paint-visualizer" },
];

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function AccountControl() {
  const { user } = useUser();
  const { signOut } = useClerk();

  return (
    <>
      <Show when="signed-out">
        <Link
          href="/sign-in"
          className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          Sign in
        </Link>
      </Show>
      <Show when="signed-in">
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-sm text-muted-foreground">
            {user?.firstName ?? "Account"}
          </span>
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: basePath || "/" })}
            className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
          >
            Sign out
          </button>
        </div>
      </Show>
    </>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef   = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Close on route change, restore focus to trigger
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Restore focus to hamburger when menu closes
  useEffect(() => {
    if (!menuOpen) {
      triggerRef.current?.focus();
    }
  }, [menuOpen]);

  // Move focus into menu when it opens; trap focus inside
  useEffect(() => {
    if (!menuOpen || !menuRef.current) return;

    const menuEl = menuRef.current;
    const focusable = () =>
      Array.from(menuEl.querySelectorAll<HTMLElement>(FOCUSABLE));

    // Move focus to first item
    const first = focusable()[0];
    first?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== "Tab") return;

      const items = focusable();
      if (!items.length) return;

      const last  = items[items.length - 1];
      const first = items[0];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen, closeMenu]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Header height — used to position overlay below it
  // py-4 top+bottom = 2rem; icon 44px ≈ total ~60px; use CSS var for accuracy
  const headerHeight = "61px";

  return (
    <div className="min-h-[100dvh] flex flex-col selection:bg-primary/20 selection:text-primary">
      {/* ── Header ── */}
      <header className="py-[10px] px-5 sm:py-6 sm:px-6 md:px-12 flex items-center justify-between z-40 sticky top-0 bg-background/80 backdrop-blur-md border-b border-border/40">
        <Link
          href="/"
          className="font-serif text-xl font-medium tracking-tight text-foreground hover:text-primary transition-colors"
        >
          Mychromamind
        </Link>

        {/* Desktop nav */}
        <nav aria-label="Main navigation" className="hidden md:flex items-center gap-6 text-sm font-medium">
          {NAV_LINKS.map(({ href, label, match }) => (
            <Link
              key={href}
              href={href}
              className={cn(
                "transition-colors hover:text-primary",
                match(location) ? "text-primary" : "text-muted-foreground"
              )}
            >
              {label}
            </Link>
          ))}
          <span className="w-px h-4 bg-border" aria-hidden="true" />
          <AccountControl />
        </nav>

        {/* Mobile hamburger — 44×44 tap target */}
        <button
          ref={triggerRef}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          className="md:hidden flex flex-col justify-center items-center w-11 h-11 -mr-1 rounded-md text-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span
            className={cn(
              "block w-5 h-0.5 bg-current rounded-full transition-all duration-200",
              menuOpen ? "translate-y-[3px] rotate-45" : "-translate-y-[3px]"
            )}
          />
          <span
            className={cn(
              "block w-5 h-0.5 bg-current rounded-full transition-all duration-200",
              menuOpen ? "opacity-0 scale-x-0" : "opacity-100 scale-x-100"
            )}
          />
          <span
            className={cn(
              "block w-5 h-0.5 bg-current rounded-full transition-all duration-200",
              menuOpen ? "-translate-y-[3px] -rotate-45" : "translate-y-[3px]"
            )}
          />
        </button>
      </header>

      {/* ── Mobile slide-down menu ── */}
      <div
        id="mobile-menu"
        ref={menuRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{ top: headerHeight }}
        className={cn(
          "md:hidden fixed inset-x-0 bottom-0 z-30 bg-background/98 backdrop-blur-md transition-all duration-300 flex flex-col overflow-y-auto",
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >
        <nav aria-label="Mobile navigation" className="flex flex-col px-6 pt-6 pb-4 gap-0">
          {NAV_LINKS.map(({ href, label, match }) => (
            <Link
              key={href}
              href={href}
              className={cn(
                "text-2xl font-serif font-medium py-4 border-b border-border/40 transition-colors min-h-[64px] flex items-center",
                match(location) ? "text-primary" : "text-foreground hover:text-primary"
              )}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Account control */}
        <div className="px-6 pt-2 pb-4 border-t border-border/40">
          <AccountControl />
        </div>

        {/* Policy links at bottom of drawer */}
        <div className="mt-auto px-6 pb-10 pt-4 flex flex-wrap gap-x-5 gap-y-3 text-xs text-muted-foreground">
          <Link href="/about"      className="hover:text-primary transition-colors underline underline-offset-4 min-h-[44px] flex items-center">About</Link>
          <Link href="/disclaimer" className="hover:text-primary transition-colors underline underline-offset-4 min-h-[44px] flex items-center">Disclaimer</Link>
          <Link href="/privacy"    className="hover:text-primary transition-colors underline underline-offset-4 min-h-[44px] flex items-center">Privacy Policy</Link>
          <Link href="/terms"      className="hover:text-primary transition-colors underline underline-offset-4 min-h-[44px] flex items-center">Terms of Service</Link>
          <Link href="/contact"    className="hover:text-primary transition-colors underline underline-offset-4 min-h-[44px] flex items-center">Contact</Link>
        </div>
      </div>

      {/* ── Page content ── */}
      <main className="flex-1 flex flex-col relative">
        {children}
      </main>

      <footer className="py-8 px-5 sm:px-6 md:px-12 text-center text-xs text-muted-foreground border-t border-border/40 space-y-3">
        <p className="max-w-xl mx-auto">
          For entertainment and self-reflection only — not psychological, medical, legal, or
          financial advice.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link href="/learn"      className="hover:text-primary transition-colors underline underline-offset-4">Color Library</Link>
          <Link href="/about"      className="hover:text-primary transition-colors underline underline-offset-4">About</Link>
          <Link href="/disclaimer" className="hover:text-primary transition-colors underline underline-offset-4">Disclaimer</Link>
          <Link href="/privacy"    className="hover:text-primary transition-colors underline underline-offset-4">Privacy Policy</Link>
          <Link href="/terms"      className="hover:text-primary transition-colors underline underline-offset-4">Terms of Service</Link>
          <Link href="/contact"    className="hover:text-primary transition-colors underline underline-offset-4">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
